import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryString } from '../hooks/useQueryString';
import { webConfig } from '../utils/webConfig';
import { HeartLoading } from '../components/HeartLoading';
import SomethingWentWrong from '../components/SomethingWentWrong';
import {
    getLastDoneeIdFromLocalStorage,
    pushDoneeIds,
} from '../components/RedirectToDonee';
import { ROUTES, applicationRoutes } from '../utils/routes';
import { ApiHandler, useDonee } from '../api';
import { usePushEvents } from '../hooks/PushEvents';
import { doneeNotFound } from '../hooks/PushEvents/clevertapEvents';
import { logError } from '../utils/logError';

const veryOldRoute = '/selection';
const oldRoute = '/donation/amount';
const getRouteDoneeId = (pathname: string) => {
    if (applicationRoutes.includes(pathname)) return undefined;
    if (pathname.includes(oldRoute)) {
        pathname = pathname.split(oldRoute)[0];
    }
    if (pathname.includes(veryOldRoute)) {
        pathname = pathname.split(veryOldRoute)[0];
    }
    const split = pathname.split('/');
    if (split.length !== 2) return;
    return split[1];
};

const getDoneeId = (doneeIdStr: string | undefined) => {
    let doneeId: string | undefined;

    if (doneeIdStr) {
        const doneeIdArr = doneeIdStr.split(webConfig.doneeIdHashPrefix);
        // allowed pattern: <name>-<city>-<state>-base64(<donee_id>
        if (doneeIdArr.length === 2) {
            // Corrected for lint rule prefer-destructuring
            [, doneeId] = doneeIdArr;
            sessionStorage.setItem('doneeId', JSON.stringify(doneeId));
        } else {
            doneeId = doneeIdStr;
        }
    }

    return doneeId;
};

const pathExceptions = [
    ROUTES.activate,
    ROUTES.setPassword,
    ROUTES.activateEmail,
    ROUTES.appClip,
    ROUTES.event,
];
const isPathException = (path: string): boolean => {
    if (path.startsWith('/donate/events')) return true;
    for (const p of pathExceptions) {
        if (path.endsWith(p)) return true;
    }
    return false;
};

const DoneeLoader: React.FC<PropsWithChildren> = ({ children }) => {
    const location = useLocation();
    const query = useQueryString();
    const doneeId = getRouteDoneeId(location.pathname);
    const id = useMemo<string | undefined>(
        () => doneeId || query.get('doneeId') || query.get('id') || undefined,
        [doneeId, query],
    );
    const { pushEvent } = usePushEvents();
    const { hasNeverFetched, fetchDonee, isLoading, error } = useDonee();
    const [tokenLoadError, setTokenLoadError] = useState(false);

    useEffect(() => {
        if (isPathException(window.location.pathname)) {
            return;
        }
        const loadId = getDoneeId(id || getLastDoneeIdFromLocalStorage());
        if (!loadId || !hasNeverFetched) {
            return;
        }
        ApiHandler.instance.onlineGiving.getClientAccessToken().then((x) => {
            if (x.success) {
                fetchDonee(loadId, x.data.response.payload.accessToken).then(
                    (donee) => {
                        if (!donee) {
                            pushEvent(doneeNotFound, {
                                path: window.location.href,
                            });
                            return;
                        }
                        pushDoneeIds(id);
                    },
                );
            } else {
                if (x.error.status === 500 && !x.error.isAxiosError) {
                    logError('API Request Error: 500', x.error);
                }
                setTokenLoadError(true);
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isPathException(window.location.pathname)) {
        return children;
    }

    if ((isLoading || hasNeverFetched) && !tokenLoadError) {
        return (
            <HeartLoading
                maxWidth={500}
                className="heart-loading"
            />
        );
    }

    if (error || tokenLoadError) {
        return <SomethingWentWrong />;
    }

    return children;
};

export default React.memo(DoneeLoader);
